import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { nonDraggablesTypeSlide } from "@/lib/constants/slides";
import { Dnd } from "@/lib/dnd/components/Dnd";
import { useAddShapeElement } from "@/lib/hooks/slides/editor/useAddShapeElement";
import { useAppSelector } from "@/store/hooks";
import type { ArrowType, LineStyle, ShapeType } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { LineRenderer } from "../../editor/renderers/LineRenderer";
import { ShapeRenderer } from "../../editor/renderers/ShapeRenderer";
import { DisabledLockOverlay } from "./DisabledLockOverlay";

export const shapes = [
  "rectangle",
  "circle",
  "triangle",
  "diamond",
  "hexagon",
  "star",
  "heart",
  "cross",
  "sun",
  "arrow",
  "double-arrow",
  "tunnel",
] as const satisfies readonly ShapeType[];

interface LineConfig {
  id: string;
  label: string;
  lineStyle: LineStyle;
  startArrow?: ArrowType;
  endArrow?: ArrowType;
}

const lines: LineConfig[] = [
  { id: "solid", label: "Solid", lineStyle: "solid" },
  { id: "dashed", label: "Dashed", lineStyle: "dashed" },
  { id: "dotted", label: "Dotted", lineStyle: "dotted" },
  { id: "arrow", label: "Arrow", lineStyle: "solid", endArrow: "arrow" },
  { id: "diamond", label: "Arrow", lineStyle: "solid", endArrow: "diamond" },
  {
    id: "circle-end",
    label: "Double Arrow",
    lineStyle: "solid",
    endArrow: "circle",
  },
  {
    id: "double-arrow",
    label: "Circle End",
    lineStyle: "solid",
    endArrow: "arrow",
    startArrow: "arrow",
  },
  {
    id: "double-circle",
    label: "Diamond End",
    lineStyle: "solid",
    endArrow: "circle",
    startArrow: "circle",
  },
  {
    id: "double-diamond",
    label: "Square End",
    lineStyle: "solid",
    endArrow: "square",
    startArrow: "square",
  },
];

function capitalizeLabel(name: string) {
  return name
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function ShapesPanel() {
  const { setActivePanel } = useSlideEditorContext();
  const { handleAddShapeItem, handleAddLineItem } = useAddShapeElement();

  const { slide } = useAppSelector((state) => state.slideEditor.present);

  if (!slide) return null;

  const slideType = slide.type;
  const knowledgeArea = slide.knowledgeArea;

  const shapesDisabled =
    (nonDraggablesTypeSlide[slideType] as readonly string[])?.includes(knowledgeArea) ?? false;

  const linesDisabled =
    (nonDraggablesTypeSlide[slideType] as readonly string[])?.includes(knowledgeArea) ?? false;

  return (
    <div className="px-2">
      <div className="mb-4">
        <p className="text-sm font-semibold  uppercase tracking-wider px-1">Shapes</p>
        <p className="text-xs text-slate-400  mt-1 px-1">Click to add to your slide</p>
      </div>

      <div className="relative">
        <div
          className={`grid grid-cols-3 gap-3 transition-all duration-300 ${
            shapesDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
          }`}
        >
          {shapes.map((shape) => {
            const label = capitalizeLabel(shape);

            return (
              <Dnd
                key={shape}
                id={`shape-${shape}`}
                data={{
                  id: shape,
                  type: "shape",
                }}
              >
                <IconButton
                  key={shape}
                  onClick={() => {
                    handleAddShapeItem(shape);
                    setActivePanel(null);
                  }}
                  variant="ghost"
                  className="group relative aspect-square rounded-xl bg-white border-2 border-slate-200 hover:border-primary-400 transition-all duration-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm cursor-pointer overflow-hidden"
                  title={label}
                  aria-label={`Add ${label}`}
                  disabled={shapesDisabled}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-accent-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                  <div className="relative flex items-center justify-center h-full p-4 group-hover:scale-110 transition-transform duration-200">
                    <ShapeRenderer element={{ shapeType: shape }} />
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <span className="text-[10px] font-medium text-white drop-shadow-sm block text-center">
                      {label}
                    </span>
                  </div>
                </IconButton>
              </Dnd>
            );
          })}
        </div>

        {shapesDisabled && (
          <DisabledLockOverlay
            title="Shapes Not Available"
            description="This slide type doesn't support shape elements"
          />
        )}
      </div>

      <div className="my-6" />

      <div className="mb-4">
        <p className="text-sm font-semibold uppercase tracking-wider px-1">Lines</p>
        <p className="text-xs text-slate-400 mt-1 px-1">Click to add to your slide</p>
      </div>

      <div className="relative">
        <div
          className={`grid grid-cols-3 gap-3 transition-all duration-300 ${linesDisabled ? "opacity-40 blur-[2px]" : ""}`}
        >
          {lines.map((line) => (
            <Dnd
              key={line.id}
              id={`line-${line.id}`}
              data={{
                id: line.id,
                type: "line",
                data: line,
              }}
              className="w-full h-full"
            >
              <button
                type="button"
                key={line.id}
                title={line.label}
                aria-label={`Add ${line.label}`}
                onClick={() => {
                  handleAddLineItem(line);
                  setActivePanel(null);
                }}
                className="group w-full h-full flex items-center justify-center relative aspect-square rounded-xl border-2 bg-white border-slate-200 hover:border-primary-400 transition-all duration-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm cursor-pointer overflow-hidden"
                disabled={linesDisabled}
              >
                <div className="absolute top-9 flex items-center justify-center h-full p-6">
                  <LineRenderer
                    element={{
                      id: line.id,
                      startX: -20,
                      startY: 30,
                      endX: 70,
                      endY: 30,
                      stroke: "#000000",
                      strokeWidth: 3,
                      lineStyle: line.lineStyle,
                      startArrow: line.startArrow,
                      endArrow: line.endArrow,
                    }}
                  />
                </div>

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <span className="text-[10px] font-medium text-white drop-shadow-sm block text-center">
                    {line.label}
                  </span>
                </div>
              </button>
            </Dnd>
          ))}
        </div>

        {linesDisabled && (
          <DisabledLockOverlay
            title="Lines Not Available"
            description="This slide type doesn't support line elements"
          />
        )}
      </div>

      <div className="mt-6 p-3 bg-primary-50/50 border border-primary-100 rounded-lg">
        <p className="text-xs text-primary-700 text-center">
          <span className="font-semibold">Pro tip:</span> Click elements to position them on your
          slide
        </p>
      </div>
    </div>
  );
}
